import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOWN_ARROW } from '@angular/cdk/keycodes';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Optional,
  Output
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';
import { AutoUnsubscribe, NK_FORMATTER, NK_PARSER } from '@nitro/core/common';
import { Subscription } from 'rxjs';
import { NkPicker } from './picker';

/** @docs-private */
export const NK_PICKER_INPUT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NkPickerInput),
  multi: true
};

/** @docs-private */
export const NK_PICKER_INPUT_VALIDATORS: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => NkPickerInput),
  multi: true
};

/**
 * An event used for picker input and change events. We don't always have access to a native
 * input or change event because the event may have been triggered by the user clicking on the
 * calendar popup. For consistency, we always use NkPickerInputEvent instead.
 */
export class NkPickerInputEvent<V> {
  /** The new value for the target picker input. */
  value: V | null;

  constructor(
    /** Reference to the picker input component that emitted the event. */
    public target: NkPickerInput<V>,
    /** Reference to the native input element associated with the picker input. */
    public targetElement: HTMLElement
  ) {
    this.value = this.target.value;
  }
}

/** Directive used to connect an input to a NkPicker. */
// eslint:disable-next-line: max-classes-per-file
@AutoUnsubscribe()
@Directive({
  selector: 'input[nkPicker]', // eslint-disable-line
  providers: [
    NK_PICKER_INPUT_VALUE_ACCESSOR,
    NK_PICKER_INPUT_VALIDATORS,
    // TODO: (TSLINT) fix lint error: 'NkPickerInput' was used before it was defined
    // 	@typescript-eslint/no-use-before-define
    { provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: NkPickerInput } // eslint-disable-line
  ],
  host: {
    'aria-haspopup': 'dialog',
    '[attr.aria-owns]': '(_picker?.opened && _picker.id) || null',
    '[disabled]': 'disabled',
    // '(input)': '_onInput($event.target.value)',
    // '(change)': '_onChange()',
    '(blur)': '_onBlur($event.target.value)',
    '(keydown)': '_onKeydown($event)'
  },
  exportAs: 'nkPickerInput'
})
export class NkPickerInput<V> implements ControlValueAccessor, OnDestroy, Validator {
  _controlErrors: ValidationErrors;

  /** The picker that this input is associated with. */
  @Input()
  set nkPicker(picker: NkPicker<V>) {
    if (!picker) {
      throw Error(`NkPicker: No picker control attached`);
    }
    this._picker = picker;
    this._picker._registerInput(this);
    this._pickerSubscription.unsubscribe();

    this._picker.pickerControl.validStateChange.subscribe((errs: ValidationErrors) => {
      this._controlErrors = errs;
      this._validatorOnChange();
    });

    this._pickerSubscription = this._picker.closedStream.subscribe((selected: V) => {
      if (selected !== void 0) {
        this._parsed = false;
        this.value = selected;
        this._cvaOnChange(selected);
        this._onTouched();
        this.valueInput.emit(new NkPickerInputEvent(this, this._elementRef.nativeElement));
        this.valueChange.emit(selected);
      }
    });
  }

  /** The value of the input. */
  @Input()
  get value(): V | null {
    return this._value;
  }
  set value(value: V | null) {
    // value = this._dateAdapter.deserialize(value);
    // this._lastValueValid = !value || this._dateAdapter.isValid(value);
    // value = this._getValidDateOrNull(value);

    this._value = value;

    if (this._picker) {
      this._picker.pickerControl.value = value || null;
    }

    if (!this._parsed) {
      this._formatValue(value);
    }
    if (this._lastValue !== value) {
      this._lastValue = value;
      this.valueChange.emit(value);
    }
    this._parsed = false;
    this._changeRef.markForCheck();
  }

  private _formatter: NK_FORMATTER<V> | null;
  @Input()
  public get formatter(): NK_FORMATTER<V> | null {
    if (this._formatter) {
      return this._formatter;
    }
    if (this._picker && this._picker.pickerControl && this._picker.pickerControl.formatter) {
      return this._picker.pickerControl.formatter;
    }
    return null;
  }
  public set formatter(value: NK_FORMATTER<V> | null) {
    this._formatter = value;
  }
  @Input() displayFormat: string;

  private _parser: NK_PARSER<V> | null;
  @Input()
  public get parser(): NK_PARSER<V> | null {
    if (this._parser) {
      return this._parser;
    }
    if (this._picker && this._picker.pickerControl && this._picker.pickerControl.parser) {
      return this._picker.pickerControl.parser;
    }
    return null;
  }
  public set parser(_p: NK_PARSER<V> | null) {
    this._parser = _p;
  }

  /** Whether the picker-input is disabled. */
  @Input()
  get disabled(): boolean {
    return coerceBooleanProperty(this._disabled);
  }
  set disabled(value: boolean) {
    const newValue = coerceBooleanProperty(value);
    const element = this._elementRef.nativeElement;

    if (this._disabled !== newValue) {
      this._disabledChange.emit(newValue);
    }

    this._disabled = newValue;

    // We need to null check the `blur` method, because it's undefined during SSR.
    if (newValue && element.blur) {
      // Normally, native input elements automatically blur if they turn disabled. This behavior
      // is problematic, because it would mean that it triggers another change detection cycle,
      // which then causes a changed after checked error if the input element was focused before.
      element.blur();
    }
  }

  constructor(
    private _elementRef: ElementRef<HTMLInputElement>,
    private _changeRef: ChangeDetectorRef,
    @Optional() private _formField: MatFormField
  ) {}

  _picker: NkPicker<V>;
  private _value: V | null;
  private _parsed = false;
  private _disabled: boolean;

  /** Emits when a `change` event is fired on this `<input>`. */
  @Output() readonly valueChange = new EventEmitter<V | null>();

  /** Emits when an `input` event is fired on this `<input>`. */
  @Output()
  readonly valueInput: EventEmitter<NkPickerInputEvent<V>> = new EventEmitter<NkPickerInputEvent<V>>();

  // /** Emits when the value changes (either due to user input or programmatic change). */
  // _valueChange = new EventEmitter<V|null>();

  /** Emits when the disabled state has changed */
  _disabledChange = new EventEmitter<boolean>();

  private _pickerSubscription = Subscription.EMPTY;

  // private _localeSubscription = Subscription.EMPTY;

  /** Whether the last value set on the input was valid. */
  private _lastValue: V | null;
  private _lastInput = '';

  _onTouched = () => {};

  private _cvaOnChange: (value: any) => void = () => {};

  private _validatorOnChange = () => {};

  ngOnDestroy() {
    if (this._pickerSubscription) {
      this._pickerSubscription.unsubscribe();
    }
    // this._localeSubscription.unsubscribe();
    // this._valueChange.complete();
    this._disabledChange.complete();
  }

  /** @docs-private */
  registerOnValidatorChange(fn: () => void): void {
    this._validatorOnChange = fn;
  }

  /** @docs-private */
  validate(c: AbstractControl): ValidationErrors | null {
    setTimeout(() => {
      this._changeRef.markForCheck();
    });

    if (this._controlErrors) {
      return this._controlErrors;
    }

    return (
      this.value &&
      (this._picker.pickerControl.validator ? this._picker.pickerControl.validator(this._picker.pickerControl) : null)
    );
  }

  // /**
  //  * @deprecated
  //  * @breaking-change 8.0.0 Use `getConnectedOverlayOrigin` instead
  //  */
  // getPopupConnectionElementRef(): ElementRef {
  //   return this.getConnectedOverlayOrigin();
  // }

  /**
   * Gets the element that the picker popup should be connected to.
   * @return The element to connect the popup to.
   */
  getConnectedOverlayOrigin(): ElementRef {
    return this._formField ? this._formField.getConnectedOverlayOrigin() : this._elementRef;
  }

  // Implemented as part of ControlValueAccessor.
  writeValue(value: V): void {
    this.value = value;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: any) => void): void {
    this._cvaOnChange = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  // Implemented as part of ControlValueAccessor.
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  _onKeydown(event: KeyboardEvent) {
    const isAltDownArrow = event.altKey && event.keyCode === DOWN_ARROW;
    if (this._picker && isAltDownArrow && !this._elementRef.nativeElement.readOnly) {
      this._picker.open();
      event.preventDefault();
    }
  }

  parse(value: string) {
    const v = (!value ? null : this.parser ? this.parser(value) : value) as V;
    //    this._lastValueValid = !v || !this.validator || this.validator(v);

    this._elementRef.nativeElement.value = !v ? '' : value;
    this._parsed = true;

    if (this._lastValue !== v) {
      this._lastValue = v;
      this._value = v;
      this._cvaOnChange(v);
      //      this._validatorOnChange();
      // this._valueChange.emit(v);
      this.valueChange.emit(v);
      this._picker.pickerControl.value = this._value;
      this._picker.pickerControl._onValidatorChange();
      this.valueInput.emit(new NkPickerInputEvent(this, this._elementRef.nativeElement));
    }
  }

  // _onChange(v: V|null) {
  //   this.valueChange.emit(v);
  // }

  /** Returns the palette used by the input's form field, if any. */
  _getThemePalette(): ThemePalette {
    return this._formField ? this._formField.color : undefined;
  }

  /** Handles blur events on the input. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _onBlur(val: any): void {
    // TODO: (RADU) set this as an option
    // Reformat the input only if we have a valid value.
    // if (this.value) {
    //   this._formatValue(this.value);
    // }
    if (this._lastInput !== val) {
      this.parse(val);
      this._onTouched();
      this._lastInput = val;
    }
    //    this.valueChange.emit(this._value);
  }

  /** Formats a value and sets it on the input element. */
  private _formatValue(value: V | null) {
    // @ts-ignore
    this._elementRef.nativeElement.value = value
      ? this.formatter
        ? // @ts-ignore
          this.formatter(value, this.displayFormat)
        : // @ts-ignore
          <string>value.toString()
      : '';
    this._lastInput = this._elementRef.nativeElement.value;
  }
}
